'use client'

import { updateNotificationSettings } from '@/app/profile-manager/settings/notification-preferences/actions'
import Loader from '@/components/common/loader'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { useUnsavedChanges } from '@/hooks/use-unsaved-changes'
import { cn } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { Info } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'

const formSchema = z
  .object({
    optionalEmail: z.boolean(),
    discord: z.boolean(),
    topics: z
      .object({
        new_events: z.boolean(),
        new_championships: z.boolean(),
        racing_activity: z.boolean(),
        event_start_reminders: z.boolean(),
        results_upload_reminders: z.boolean(),
      })
      .strict(),
  })
  .strict()

function switchClassName(enabled, dirty) {
  return cn(
    enabled ? 'dark:bg-success-500!' : 'dark:bg-error-500!',
    dirty && 'border-2! border-warning!',
  )
}

export default function NotificationSettingsForm({
  initialPreferences,
  courierSyncAvailable,
  topicOptions,
}) {
  const [isSaving, setIsSaving] = useState(false)
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: initialPreferences,
  })
  const optionalEmailEnabled = form.watch('optionalEmail')

  useUnsavedChanges(form.formState.isDirty)

  async function onSubmit(data) {
    setIsSaving(true)

    try {
      const response = await updateNotificationSettings(null, data)
      if (response.success) {
        toast.success(response.message)
        form.reset(data)
      } else {
        toast.error(response.message)
      }
    } catch {
      toast.error(
        'Failed to update notification preferences. Please try again.',
      )
    } finally {
      setIsSaving(false)
    }
  }

  function handleReset() {
    form.reset(initialPreferences)
    toast.success('Changes reverted')
  }

  return (
    <div className="relative space-y-6">
      {isSaving ? (
        <Loader size="overlay" logo={false} content="Saving..." />
      ) : null}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <Card className="bg-transparent! border-none p-0 shadow-none">
            <CardHeader className="px-0 pt-0">
              <CardTitle className="text-h-4 font-semibold text-white">
                Notification preferences
              </CardTitle>
              <CardDescription className="text-p-1 text-neutral-blue-500">
                Choose which Grid Finder emails you receive.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-8 px-0">
              {!courierSyncAvailable ? (
                <div
                  role="status"
                  className="flex gap-3 border border-warning/50 bg-warning/10 p-4 text-p-1 text-white"
                >
                  <Info
                    aria-hidden="true"
                    className="mt-0.5 size-5 shrink-0 text-warning"
                  />
                  <p>
                    Courier preferences could not be checked. Your Grid Finder
                    opt-outs are still protecting optional emails.
                  </p>
                </div>
              ) : null}

              <section className="space-y-4" aria-labelledby="delivery-heading">
                <h2
                  id="delivery-heading"
                  className="text-h-5 font-semibold text-white"
                >
                  Delivery
                </h2>

                <FormField
                  control={form.control}
                  name="optionalEmail"
                  render={({ field, fieldState }) => (
                    <FormItem className="flex items-start gap-3 space-y-0">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className={switchClassName(
                            field.value,
                            fieldState.isDirty,
                          )}
                        />
                      </FormControl>
                      <div className="space-y-1">
                        <FormLabel className="text-p-1 font-normal text-white">
                          Optional email notifications
                        </FormLabel>
                        <FormDescription className="text-neutral-blue-500">
                          Turn off all optional Grid Finder emails. Marketplace
                          transaction confirmations are still sent.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="discord"
                  render={({ field, fieldState }) => (
                    <FormItem className="flex items-start gap-3 space-y-0">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className={switchClassName(
                            field.value,
                            fieldState.isDirty,
                          )}
                        />
                      </FormControl>
                      <div className="space-y-1">
                        <FormLabel className="text-p-1 font-normal text-white">
                          Discord notifications
                        </FormLabel>
                        <FormDescription className="text-neutral-blue-500">
                          Keep your existing Grid Finder Discord preference.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </section>

              <section className="space-y-4" aria-labelledby="email-heading">
                <div>
                  <h2
                    id="email-heading"
                    className="text-h-5 font-semibold text-white"
                  >
                    Optional email topics
                  </h2>
                  {!optionalEmailEnabled ? (
                    <p className="mt-1 text-p-1 text-neutral-blue-500">
                      Enable optional email notifications to change individual
                      topics.
                    </p>
                  ) : null}
                </div>

                <div className="divide-y divide-white/10 border-y border-white/10">
                  {topicOptions.map((topic) => (
                    <FormField
                      key={topic.key}
                      control={form.control}
                      name={`topics.${topic.key}`}
                      render={({ field, fieldState }) => (
                        <FormItem className="flex min-h-20 items-center justify-between gap-4 py-4">
                          <div className="min-w-0 space-y-1">
                            <FormLabel className="text-p-1 font-normal text-white">
                              {topic.label}
                            </FormLabel>
                            <FormDescription className="text-neutral-blue-500">
                              {topic.description}
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={!optionalEmailEnabled}
                              className={switchClassName(
                                field.value,
                                fieldState.isDirty,
                              )}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </section>

              <section className="space-y-3" aria-labelledby="required-heading">
                <h2
                  id="required-heading"
                  className="text-h-5 font-semibold text-white"
                >
                  Required emails
                </h2>
                <div className="flex min-h-20 items-center justify-between gap-4 border-y border-white/10 py-4">
                  <div className="min-w-0">
                    <p className="text-p-1 text-white">
                      Marketplace transactions
                    </p>
                    <p className="mt-1 text-sm text-neutral-blue-500">
                      Purchase and sale confirmations are always sent as
                      transactional records.
                    </p>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-success-500">
                    Required
                  </span>
                </div>
              </section>
            </CardContent>

            <CardFooter className="flex flex-row-reverse border-t border-white/10 px-0 pt-6">
              <Button
                type="submit"
                disabled={!form.formState.isDirty || isSaving}
                className="rounded-sm bg-white text-p-1 font-semibold text-black hover:bg-gray-200 disabled:opacity-50"
              >
                Save changes
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={!form.formState.isDirty || isSaving}
                className="mr-6 text-neutral-blue-500 underline underline-offset-2 hover:text-white disabled:opacity-50 disabled:hover:text-neutral-blue-500"
                onClick={handleReset}
              >
                Reset
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  )
}
